import React, { useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import { AuthService } from "../../Services/authServices";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { defaultTheme, BrandPanel, NxField, NxButton, NxIcon, PageShell } from "../../components/nx";

export default function SignUp() {
  const navigate = useNavigate();
  const t = defaultTheme;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", last_name: "", email: "", dob: "" as any,
    gender: "", mobile: "", password: "", cnf_password: "", city: "",
    membershipNumber: "",
  });

  // Webcam state
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setFormData({ ...formData, dob: date });
  };

  const openCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 480, height: 360 } });
      setStream(mediaStream);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 50);
    } catch {
      toast.error("Unable to access camera. Please allow camera permission.");
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedPhoto(blob);
        setPhotoPreview(URL.createObjectURL(blob));
      }
    }, "image/jpeg", 0.85);
    // Stop camera
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
    setCameraOpen(false);
  }, [stream]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    openCamera();
  }, [photoPreview, openCamera]);

  const signupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.cnf_password) {
      toast.error("Password and Confirm Password do not match");
      return;
    }
    if (!capturedPhoto) {
      toast.error("Please capture your live photo");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("last_name", formData.last_name);
      fd.append("email", formData.email);
      fd.append("dob", formData.dob instanceof Date ? formData.dob.toISOString() : formData.dob);
      fd.append("gender", formData.gender);
      fd.append("mobile", formData.mobile);
      fd.append("password", formData.password);
      fd.append("city", formData.city);
      if (formData.membershipNumber) {
        fd.append("membershipNumber", formData.membershipNumber);
      }
      fd.append("photo", capturedPhoto, "photo.jpg");

      const res = await AuthService.signup(fd);
      if (res.status === 200) {
        toast.success("Account created successfully");
        window.location.href = "/login";
      }
    } catch (e: any) {
      toast.error(e.response?.data || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell t={t} style={{ display: "flex", height: "100vh" }}>
      {/* Brand panel — left */}
      <div style={{ width: "40%", height: "100%" }}>
        <BrandPanel t={t} />
      </div>

      {/* Form — right */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32, overflow: "auto" }}>
        <form onSubmit={signupSubmit} style={{ width: "100%", maxWidth: 480 }}>
          {/* Steps */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, fontSize: 11, color: t.inkMute, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            <span style={{ color: t.ink, fontWeight: 500 }}>Account</span>
            <span style={{ flex: 1, height: 1, background: t.border, marginInline: 4 }} />
          </div>

          <h1 style={{
            fontFamily: '"Instrument Serif", serif',
            fontSize: 32, fontWeight: 400, lineHeight: 1.1, letterSpacing: "-0.01em",
            color: t.ink, margin: "0 0 6px",
          }}>Create your account</h1>
          <div style={{ fontSize: 13, color: t.inkSoft, marginBottom: 24 }}>
            We just need a few details to set you up as a candidate.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <NxField t={t} label="First name" name="name" value={formData.name} placeholder="Anika" onChange={onChangeValue} />
            <NxField t={t} label="Last name" name="last_name" value={formData.last_name} placeholder="Patel" onChange={onChangeValue} />
          </div>

          <div style={{ marginTop: 14 }}>
            <NxField t={t} label="Email" name="email" type="email" value={formData.email} placeholder="you@example.com" onChange={onChangeValue} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: t.inkMute, marginBottom: 6 }}>
                Date of birth
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                placeholderText="dd / mm / yyyy"
                dateFormat="dd / MM / yyyy"
                className="nx-datepicker"
                wrapperClassName="nx-datepicker-wrapper"
              />
            </div>
            <NxField
              t={t} label="Gender" name="gender" value={formData.gender}
              options={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "other", label: "Other" }]}
              onChange={onChangeValue}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 14, marginTop: 14 }}>
            <NxField t={t} label="Mobile" name="mobile" value={formData.mobile} placeholder="+91 98XXX XXX12" onChange={onChangeValue} />
            <NxField t={t} label="City" name="city" value={formData.city} placeholder="e.g. Bengaluru" onChange={onChangeValue} />
          </div>

          <div style={{ marginTop: 14 }}>
            <NxField t={t} label="Membership number (optional)" name="membershipNumber" value={formData.membershipNumber} placeholder="e.g. MEM-00123" onChange={onChangeValue} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
            <NxField t={t} label="Password" name="password" type="password" value={formData.password} onChange={onChangeValue} helper="Min. 10 characters, mixed case." />
            <NxField t={t} label="Confirm password" name="cnf_password" type="password" value={formData.cnf_password} onChange={onChangeValue} />
          </div>

          {/* Live Photo Capture */}
          <div style={{ marginTop: 18 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: t.inkMute, marginBottom: 8 }}>
              Live photo <span style={{ color: t.danger }}>*</span>
            </label>

            {!photoPreview && !cameraOpen && (
              <button
                type="button"
                onClick={openCamera}
                style={{
                  width: "100%", padding: "14px 0", background: t.surface,
                  border: `1.5px dashed ${t.border}`, borderRadius: 10,
                  color: t.inkSoft, fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Open camera &amp; capture photo
              </button>
            )}

            {cameraOpen && (
              <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${t.border}`, background: "#000" }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: "100%", display: "block" }}
                />
                <div style={{ display: "flex", justifyContent: "center", padding: 10, background: t.surface }}>
                  <button
                    type="button"
                    onClick={capturePhoto}
                    style={{
                      padding: "8px 24px", background: t.accent, color: t.accentInk,
                      border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer",
                    }}
                  >
                    Capture
                  </button>
                </div>
              </div>
            )}

            {photoPreview && (
              <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${t.border}` }}>
                <img src={photoPreview} alt="Captured" style={{ width: "100%", display: "block" }} />
                <div style={{ display: "flex", justifyContent: "center", padding: 10, background: t.surface }}>
                  <button
                    type="button"
                    onClick={retakePhoto}
                    style={{
                      padding: "8px 24px", background: "transparent", color: t.inkSoft,
                      border: `1px solid ${t.border}`, borderRadius: 6, fontSize: 13, cursor: "pointer",
                    }}
                  >
                    Retake
                  </button>
                </div>
              </div>
            )}

            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 20 }}>
            <NxButton t={t} kind="primary" icon={NxIcon.arrowRight("#fff")} w="100%" type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create account"}
            </NxButton>
          </div>

          <div style={{ textAlign: "center", fontSize: 13, color: t.inkSoft, marginTop: 16 }}>
            Already a candidate?{" "}
            <a
              style={{ color: t.ink, fontWeight: 500, textDecoration: "none", borderBottom: `1px solid ${t.ink}`, cursor: "pointer" }}
              onClick={() => navigate("/login")}
            >
              Sign in
            </a>
          </div>
        </form>
      </div>
    </PageShell>
  );
}
